/* cf-extended-spigots.js — Click Fencing, 2026-09-24
 *
 * Pete, 24 Sep: "put the extended spigots in the store and as options in the glass
 * calculator." The store side is done (product 7345803755614, handle fencing-spigot,
 * "Frameless Glass Pool Fencing Spigots — Base Mount & Core Drill"). This adds the
 * calculator side: when Spigot Type is Core Drill, a "Spigot depth" choice appears —
 * Standard 240mm (default, unchanged), Extended 360mm, Heavy-duty extended shaft
 * 360mm, or Long 780mm (600mm extended core). All three extended options are
 * MIRROR ONLY; on a black/white job they are disabled with an explanation rather
 * than silently ordering mirror parts on a black fence.
 *
 * WHY A PATCH, NOT A SOURCE EDIT. The glass calculator (ullc-calc.js) is the one
 * instance-reachable calculator (window.glassCalculator, class GlassFencingCalculator
 * on its prototype). Per click-fencing-calculators: "Glass -> wrap prototype methods
 * directly. Clean and testable." Three methods are wrapped:
 *
 *   1. createSectionForm(sectionNum) — after the section form is built, injects a
 *      "Spigot depth" <select> right after the per-section Spigot Type control, same
 *      .cf-persec-finish class so it shows/hides with the rest of "configure this
 *      section individually" exactly like every other per-section finish field.
 *   2. resolveSectionFinish(section) — adds .spigotDepth to the object every other
 *      part of the calculator already reads finishes from, using the same
 *      individual/shared/DOM/default resolution order as spigotType itself, and
 *      forcing 'standard' whenever the resolved fixing isn't exactly "Core Drill" or
 *      the resolved finish isn't mirror.
 *   3. _calcSectionQuote(sectionNum, section, calculation) — after the original runs,
 *      finds the "Hardware" spigot line it just built and, if this section's resolved
 *      depth isn't 'standard', repoints it at the extended variant: live Shopify price
 *      by SKU wins (same precedence the native code already uses), the SKU/variant
 *      ids below are the fallback. subtotal is adjusted by the same delta so the
 *      section and job totals stay exact.
 *
 * EVERYTHING DOWNSTREAM READS THE BREAKDOWN LINE'S variantId/sku, so nothing else
 * needs to change: state.quoteBreakdown IS this corrected line; the saved quote,
 * quote-review, the PDF and the CRM enquiry ([sku:|vid:] via cf-sku-stamp.js) all
 * read it; cf-real-line-items.js resolves the real /cart/add.js line from the same
 * variantId; cf-price-parity.js re-prices from /products.json by the same variantId;
 * cf-calc-price-lock.js's lock maps do not contain these new variant ids/SKUs at all,
 * so it never touches them (checked — extended spigots are new stock, not part of
 * the uFenceIt price-match cut list); cf-quote-truth.js's isStraySpigot() is scoped
 * to calc === 'flattop' only, so glass spigots (standard or extended) are untouched.
 *
 * Standard (unpicked) behaviour is byte-for-byte unchanged: when depth resolves to
 * 'standard' this patch does not touch the breakdown line at all.
 *
 * Self-installing, idempotent, every patch wrapped in try/catch — a display/UI
 * feature must never break the calculator. Load anywhere after ullc-calc.js in
 * meta-tags.liquid; only runs on pages where window.glassCalculator's prototype is
 * GlassFencingCalculator (i.e. only wires up where the class exists at all — the
 * page-specific glass-fencing behaviour, e.g. showing the UI, still only matters on
 * /pages/glass-fencing since that's the only page with a spigot product wired up).
 */
(function () {
  'use strict';
  if (window.__cfExtendedSpigots) return;
  window.__cfExtendedSpigots = true;

  // priceCents below is the LAST-RESORT fallback only, used solely if the live
  // store price cannot be read at all (product feed not yet loaded / variant
  // missing from shopifyData). The real price always comes from
  // window.CF_PRICE(sku) first, then the live variant on shopifyData.products.spigot,
  // in that order — see resolveLivePriceCents(). Pete corrected these once already
  // (2026-09-24, same day: 30% gross margin re-price on the store — 46311 $82.23,
  // 49981 $116.77, 48625 $94.16 inc GST) and the whole point of reading CF_PRICE
  // live is that this file never has to be edited again for a price change.
  var DEPTH_OPTIONS = [
    { key: 'standard', label: 'Standard 240mm', help: 'The size we supply as standard — no change needed for a normal footing.' },
    { key: 'extended-core-drill', label: 'Extended 360mm', desc: 'Extended Core Drill 360mm', sku: '46311', variantId: 48949559296094, priceCents: 8223,
      help: 'For core drilling through pavers into the footing underneath.' },
    { key: 'extended-shaft', label: 'Heavy-duty extended shaft 360mm', desc: 'Extended Shaft 360mm (Heavy Duty)', sku: '49981', variantId: 48949559328862, priceCents: 11677,
      help: 'A stronger one-piece shaft with the same 360mm reach.' },
    { key: 'long-core-drill', label: 'Long 780mm (600mm extended core)', desc: 'Long Core Drill 780mm', sku: '48625', variantId: 48949559361630, priceCents: 9416,
      help: 'For deep footings that need a long core drill.' }
  ];
  var DEPTH_BY_KEY = {};
  DEPTH_OPTIONS.forEach(function (o) { DEPTH_BY_KEY[o.key] = o; });

  function log() { try { console.log.apply(console, ['[cf-extended-spigots]'].concat([].slice.call(arguments))); } catch (e) {} }
  function warn() { try { console.warn.apply(console, ['[cf-extended-spigots]'].concat([].slice.call(arguments))); } catch (e) {} }

  // ── shared helpers ──────────────────────────────────────────────────────────
  function isCoreDrillType(t) { return /^core drill$/i.test(String(t || '').trim()); }
  function isMirror(finish) { return /^mirror$/i.test(String(finish || '').trim()); }

  function buildDepthSelectHTML(idAttr) {
    var opts = DEPTH_OPTIONS.map(function (o) {
      return '<option value="' + o.key + '">' + o.label + '</option>';
    }).join('');
    return (
      '<div class="form-group cf-persec-finish cf-spigot-depth-group" data-cf-spigot-depth-group style="display:none; margin-top:8px;">' +
        '<label style="display:flex; align-items:center; gap:8px;"><span>Spigot Depth</span></label>' +
        '<select class="form-control" id="' + idAttr + '"></select>' +
        '<div class="cf-spigot-depth-help" style="font-size:12px; color:#64748b; margin-top:4px;"></div>' +
        '<div class="cf-spigot-depth-mirror-note" style="display:none; font-size:12px; color:#b45309; margin-top:4px;">Extended spigots come in mirror only.</div>' +
      '</div>'
    );
  }

  function populateDepthSelect(sel) {
    if (!sel || sel.__cfPopulated) return;
    sel.__cfPopulated = true;
    sel.innerHTML = DEPTH_OPTIONS.map(function (o) {
      return '<option value="' + o.key + '">' + o.label + '</option>';
    }).join('');
    sel.value = 'standard';
  }

  // Enable/disable the extended options and show the mirror-only note; resets the
  // selection to 'standard' if it was pointed at an extended option that is no
  // longer allowed. Returns nothing; mutates the DOM only — the actual pricing
  // enforcement happens again, independently, in resolveSectionFinish below, so a
  // DOM mismatch can never leak an extended-priced/wrong-colour combination through.
  function applyDepthGate(group, finish, spigotType) {
    if (!group) return;
    var sel = group.querySelector('select');
    var note = group.querySelector('.cf-spigot-depth-mirror-note');
    var helpEl = group.querySelector('.cf-spigot-depth-help');
    var showGroup = isCoreDrillType(spigotType);
    group.style.display = showGroup ? 'block' : 'none';
    if (!sel) return;
    var mirrorOk = isMirror(finish);
    for (var i = 0; i < sel.options.length; i++) {
      var opt = sel.options[i];
      opt.disabled = (opt.value !== 'standard' && !mirrorOk);
    }
    if (!mirrorOk && sel.value !== 'standard') {
      sel.value = 'standard';
      try { sel.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
    }
    if (note) note.style.display = mirrorOk ? 'none' : 'block';
    if (helpEl) {
      var chosen = DEPTH_BY_KEY[sel.value] || DEPTH_BY_KEY.standard;
      helpEl.textContent = chosen.help || '';
    }
  }

  // ── 1. inject the SHARED (same-for-all) depth control, once ────────────────
  function installSharedControl(calc) {
    if (document.getElementById('cf-shared-spigot-depth-group')) return;
    var anchor = document.getElementById('shared-spigot-type-group');
    if (!anchor || !anchor.parentNode) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = buildDepthSelectHTML('spigot-depth').replace(
      'data-cf-spigot-depth-group', 'data-cf-spigot-depth-group id="cf-shared-spigot-depth-group"'
    );
    var group = wrap.firstElementChild;
    anchor.parentNode.insertBefore(group, anchor.nextSibling);
    var sel = group.querySelector('select');
    populateDepthSelect(sel);

    function refresh() {
      try {
        var fin = calc.resolveSectionFinish ? calc.resolveSectionFinish(null) : {};
        applyDepthGate(group, fin.finish, fin.spigotType);
      } catch (e) { warn('shared refresh failed', e); }
    }
    sel.addEventListener('change', function () {
      try {
        calc.state.shared = calc.state.shared || {};
        calc.state.shared.spigotDepth = sel.value;
      } catch (e) {}
      var helpEl = group.querySelector('.cf-spigot-depth-help');
      if (helpEl) helpEl.textContent = (DEPTH_BY_KEY[sel.value] || DEPTH_BY_KEY.standard).help || '';
    });
    var typeSel = document.getElementById('spigot-type');
    var colourSel = document.getElementById('spigot-shape');
    if (typeSel) typeSel.addEventListener('change', refresh);
    if (colourSel) colourSel.addEventListener('change', refresh);
    refresh();
    log('shared Spigot Depth control installed');
  }

  // ── 2. wrap createSectionForm — inject the PER-SECTION depth control ───────
  function wrapCreateSectionForm(proto) {
    if (proto.createSectionForm.__cfExtSpigotWrapped) return;
    var orig = proto.createSectionForm;
    var wrapped = function (sectionNum) {
      var form = orig.apply(this, arguments);
      try {
        var typeGroup = form.querySelector('#section-' + sectionNum + '-spigot-type');
        typeGroup = typeGroup ? typeGroup.closest('.form-group') : null;
        if (typeGroup && !form.querySelector('#section-' + sectionNum + '-spigot-depth')) {
          var wrap = document.createElement('div');
          wrap.innerHTML = buildDepthSelectHTML('section-' + sectionNum + '-spigot-depth');
          var group = wrap.firstElementChild;
          typeGroup.parentNode.insertBefore(group, typeGroup.nextSibling);
          var sel = group.querySelector('select');
          populateDepthSelect(sel);

          var calc = this;
          var refresh = function () {
            try {
              var section = calc.state.sections && calc.state.sections[sectionNum];
              var fin = calc.resolveSectionFinish(section);
              applyDepthGate(group, fin.finish, fin.spigotType);
            } catch (e) { warn('per-section refresh failed', e); }
          };
          sel.addEventListener('change', function () {
            var section = calc.state.sections && calc.state.sections[sectionNum];
            if (section) section.spigotDepth = sel.value;
            var helpEl = group.querySelector('.cf-spigot-depth-help');
            if (helpEl) helpEl.textContent = (DEPTH_BY_KEY[sel.value] || DEPTH_BY_KEY.standard).help || '';
          });
          var typeSelEl = form.querySelector('#section-' + sectionNum + '-spigot-type');
          var colourSelEl = form.querySelector('#section-' + sectionNum + '-finish');
          if (typeSelEl) typeSelEl.addEventListener('change', function () { setTimeout(refresh, 0); });
          if (colourSelEl) colourSelEl.addEventListener('change', function () { setTimeout(refresh, 0); });
          setTimeout(refresh, 0);
        }
      } catch (e) { warn('createSectionForm inject failed', e); }
      return form;
    };
    wrapped.__cfExtSpigotWrapped = true;
    proto.createSectionForm = wrapped;
  }

  // ── 3. wrap resolveSectionFinish — add .spigotDepth, enforce the rules ─────
  function wrapResolveSectionFinish(proto) {
    if (proto.resolveSectionFinish.__cfExtSpigotWrapped) return;
    var orig = proto.resolveSectionFinish;
    var wrapped = function (section) {
      var res = orig.apply(this, arguments);
      try {
        var same = !!this.state.sameForAll;
        var shared = this.state.shared || {};
        var secVal = section && section.spigotDepth;
        var sharedVal = shared.spigotDepth;
        var domEl = document.getElementById('spigot-depth');
        var domValue = domEl ? domEl.value : undefined;
        var depth;
        if (!same && secVal) depth = secVal;
        else if (same && sharedVal) depth = sharedVal;
        else if (domValue) depth = domValue;
        else depth = 'standard';

        if (!DEPTH_BY_KEY[depth]) depth = 'standard';
        // Never allowed off Core Drill, and never allowed off mirror — the two
        // guard rails the brief asks for, enforced here regardless of what the
        // DOM happens to show (a stale disabled option, a race on page load).
        if (!isCoreDrillType(res.spigotType)) depth = 'standard';
        if (depth !== 'standard' && !isMirror(res.finish)) depth = 'standard';

        res.spigotDepth = depth;
      } catch (e) { warn('resolveSectionFinish wrap failed', e); res.spigotDepth = 'standard'; }
      return res;
    };
    wrapped.__cfExtSpigotWrapped = true;
    proto.resolveSectionFinish = wrapped;
  }

  // ── 4. wrap _calcSectionQuote — repoint the spigot breakdown line ──────────
  function findVariantBySku(spigotProduct, sku) {
    if (!spigotProduct || !spigotProduct.variants) return null;
    for (var i = 0; i < spigotProduct.variants.length; i++) {
      var v = spigotProduct.variants[i];
      if (String(v.sku) === String(sku)) return v;
    }
    return null;
  }

  // Live price, cents. Precedence, each one a fresher source than the last:
  //   1. window.CF_PRICE(sku) — cf-sku-prices.js's live /products.json map, the
  //      same resolver every other calculator price already trusts, and the one
  //      cf-calc-price-lock.js wraps (harmlessly — these SKUs are not in its lock
  //      maps, checked, so it never overrides this).
  //   2. the live variant already sitting on shopifyData.products.spigot (the
  //      Liquid-rendered product, current as of this page load).
  //   3. the hardcoded fallback above, only if both of the above are unavailable.
  function resolveLivePriceCents(spigotProduct, opt) {
    try {
      if (typeof window.CF_PRICE === 'function') {
        var live = window.CF_PRICE(opt.sku);
        if (live != null && !isNaN(live)) return { cents: Number(live), variant: findVariantBySku(spigotProduct, opt.sku) };
      }
    } catch (e) {}
    var v = findVariantBySku(spigotProduct, opt.sku);
    if (v && v.available !== false && typeof v.price === 'number') return { cents: v.price, variant: v };
    return { cents: opt.priceCents, variant: v };
  }

  // ── 0. hide the extended-depth variants from the native Spigot Type list ───
  // getSpigotTypes() lists every distinct AVAILABLE variant.description on the
  // spigot product — which now includes "Extended Core Drill 360mm" etc as if
  // they were alternative FIXING METHODS alongside Core Drill / Base Mount /
  // Non Conductive Core Drill. Picked there they would price via the SPIGOT_PRICES
  // fallback (no entry for these keys -> silently falls back to plain Core Drill
  // Black pricing) with none of the mirror-only guard or the depth control below.
  // Filtering them out of the TYPE list here is what makes "Spigot Depth" the only
  // way to reach them, so every extended order goes through the guarded path.
  var EXTENDED_DESCRIPTIONS = {};
  DEPTH_OPTIONS.forEach(function (o) { if (o.desc) EXTENDED_DESCRIPTIONS[o.desc.toLowerCase()] = true; });

  function wrapGetSpigotTypes(proto) {
    if (!proto.getSpigotTypes || proto.getSpigotTypes.__cfExtSpigotWrapped) return;
    var orig = proto.getSpigotTypes;
    var wrapped = function () {
      var list = orig.apply(this, arguments) || [];
      try {
        return list.filter(function (o) { return !EXTENDED_DESCRIPTIONS[String(o.value || '').toLowerCase()]; });
      } catch (e) { return list; }
    };
    wrapped.__cfExtSpigotWrapped = true;
    proto.getSpigotTypes = wrapped;
  }

  // Belt-and-braces for the wrap above: if #spigot-type or any already-rendered
  // per-section spigot-type select already got the bad options in before this
  // script's tick caught the instance (population runs on page-load timers we
  // don't control the order of), strip them out directly. Never removes the
  // currently-selected option out from under the customer — if one of the three
  // extended descriptions is somehow already selected, leave it alone rather
  // than silently changing what they picked; the mirror/Core-Drill-only guards
  // in resolveSectionFinish still apply regardless of what the raw select says.
  function scrubSpigotTypeOptions() {
    try {
      var selects = document.querySelectorAll('#spigot-type, select[id^="section-"][id$="-spigot-type"]');
      selects.forEach(function (sel) {
        for (var i = sel.options.length - 1; i >= 0; i--) {
          var opt = sel.options[i];
          if (EXTENDED_DESCRIPTIONS[String(opt.value || '').toLowerCase()] && !opt.selected) {
            sel.removeChild(opt);
          }
        }
      });
    } catch (e) {}
  }

  function wrapCalcSectionQuote(proto) {
    if (proto._calcSectionQuote.__cfExtSpigotWrapped) return;
    var orig = proto._calcSectionQuote;
    var wrapped = function (sectionNum, section, calculation) {
      var result = orig.apply(this, arguments);
      try {
        var fin = this.resolveSectionFinish(section);
        var depth = fin.spigotDepth || 'standard';
        if (depth === 'standard') return result;
        var opt = DEPTH_BY_KEY[depth];
        if (!opt || !result || !result.breakdown) return result;

        var line = null;
        for (var i = 0; i < result.breakdown.length; i++) {
          var b = result.breakdown[i];
          if (b && b.category === 'Hardware' && /spigot/i.test(String(b.item || '')) && !/clamp/i.test(String(b.item || ''))) {
            line = b;
            break;
          }
        }
        if (!line) return result;

        var spigotProduct = this.shopifyData && this.shopifyData.products && this.shopifyData.products.spigot;
        var resolved = resolveLivePriceCents(spigotProduct, opt);
        var liveVariant = resolved.variant;
        var unitCents = resolved.cents;
        var unitPrice = unitCents / 100;
        var qty = Number(line.quantity) || 0;
        var oldTotal = Number(line.totalPrice) || 0;
        var newTotal = Math.round(qty * unitPrice * 100) / 100;

        var shape = fin.spigotShape || 'Square';
        var colourDisplay = (typeof this._colourDisplayName === 'function')
          ? this._colourDisplayName(fin.finish || 'mirror')
          : (fin.finish || 'Mirror');

        line.unitPrice = unitPrice;
        line.totalPrice = newTotal;
        line.item = opt.desc + ' Spigot ' + shape + ' ' + colourDisplay;
        line.variantId = liveVariant ? liveVariant.id : opt.variantId;
        line.sku = String(liveVariant ? liveVariant.sku : opt.sku);

        result.subtotal = (Number(result.subtotal) || 0) + (newTotal - oldTotal);
        log('section ' + sectionNum + ': spigot depth "' + depth + '" -> ' + line.item + ' @ $' + unitPrice.toFixed(2)
          + ' x' + qty + ' (was $' + oldTotal.toFixed(2) + ', now $' + newTotal.toFixed(2) + ')');
      } catch (e) {
        warn('_calcSectionQuote wrap failed — leaving native spigot line in place', e);
      }
      return result;
    };
    wrapped.__cfExtSpigotWrapped = true;
    proto._calcSectionQuote = wrapped;
  }

  // ── 5. minimal session persistence for spigotDepth (parallel to _saveSession) ──
  function wrapSessionPersistence(proto) {
    if (proto._saveSession && !proto._saveSession.__cfExtSpigotWrapped) {
      var origSave = proto._saveSession;
      var wrappedSave = function () {
        var r = origSave.apply(this, arguments);
        try {
          var depths = {};
          Object.keys(this.state.sections || {}).forEach(function (k) {
            var s = this.state.sections[k];
            if (s && s.spigotDepth) depths[k] = s.spigotDepth;
          }, this);
          var sharedDepth = (this.state.shared && this.state.shared.spigotDepth) || null;
          localStorage.setItem('cf_spigot_depth_session', JSON.stringify({ depths: depths, shared: sharedDepth }));
        } catch (e) {}
        return r;
      };
      wrappedSave.__cfExtSpigotWrapped = true;
      proto._saveSession = wrappedSave;
    }
    if (proto._restoreSession && !proto._restoreSession.__cfExtSpigotWrapped) {
      var origRestore = proto._restoreSession;
      var wrappedRestore = function () {
        var r = origRestore.apply(this, arguments);
        try {
          var raw = localStorage.getItem('cf_spigot_depth_session');
          if (raw) {
            var saved = JSON.parse(raw);
            var self = this;
            setTimeout(function () {
              try {
                if (saved.shared) {
                  self.state.shared = self.state.shared || {};
                  self.state.shared.spigotDepth = saved.shared;
                  var domEl = document.getElementById('spigot-depth');
                  if (domEl) domEl.value = saved.shared;
                }
                Object.keys(saved.depths || {}).forEach(function (k) {
                  var section = self.state.sections && self.state.sections[k];
                  if (section) section.spigotDepth = saved.depths[k];
                  var sel = document.getElementById('section-' + k + '-spigot-depth');
                  if (sel) sel.value = saved.depths[k];
                });
              } catch (e2) {}
            }, 400);
          }
        } catch (e) {}
        return r;
      };
      wrappedRestore.__cfExtSpigotWrapped = true;
      proto._restoreSession = wrappedRestore;
    }
  }

  // ── install ──────────────────────────────────────────────────────────────
  var tries = 0;
  function tick() {
    tries++;
    try {
      var calc = window.glassCalculator || window.glassFencingCalculator;
      if (calc && calc.constructor && calc.constructor.prototype && calc.constructor.prototype._calcSectionQuote) {
        var proto = calc.constructor.prototype;
        wrapGetSpigotTypes(proto);
        wrapResolveSectionFinish(proto);
        wrapCalcSectionQuote(proto);
        wrapCreateSectionForm(proto);
        scrubSpigotTypeOptions();
        wrapSessionPersistence(proto);
        // The shared control only makes sense where the shared Spigot Type group
        // exists (glass-fencing page); harmless no-op elsewhere.
        installSharedControl(calc);
        log('installed');
        return; // keep the interval running only long enough to catch the group appearing later
      }
    } catch (e) { warn('install tick failed', e); }
    if (tries < 150) setTimeout(tick, 200);
  }
  tick();

  // The shared group and per-section forms can appear well after the calculator
  // instance itself exists (step navigation), so keep re-checking for the shared
  // anchor specifically, cheaply, for the life of the page.
  setInterval(function () {
    try {
      var calc = window.glassCalculator || window.glassFencingCalculator;
      if (calc) installSharedControl(calc);
      scrubSpigotTypeOptions();
    } catch (e) {}
  }, 1000);
})();
